import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_onhnlots` raw rows into
 * `im_inventory_lots` domain rows.
 *
 * Stage 2 of the ONHNLOTS ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_onhnlots`. For each row in the latest batch:
 *   - Resolves `ltloc → im_warehouses.id` (warehouses must exist)
 *   - Resolves `ltitem → im_products_v2.id` → `im_product_variants.id`
 *     (products must exist — INITINFC is the source of truth)
 *   - Upserts into `im_inventory_lots` keyed on
 *     `(warehouseId, productId, productVariantId, unitNumber, snapshotDate)`
 */
export const transformOnhnlotsLotsStep = defineStepConfig({
  type: "transform_onhnlots_lots",
  name: "Transform ONHNLOTS → Inventory Lots",
  description:
    "Reads rexel_onhnlots rows updated by the current run, FK-resolves ltloc/ltitem against im_warehouses/im_products_v2/im_product_variants (skips unknown products with error), and upserts into im_inventory_lots.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rexel_onhnlots rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    lotsUpserted: z
      .number()
      .describe("Number of im_inventory_lots rows upserted"),
    skippedProductCount: z
      .number()
      .describe(
        "Number of distinct product SKUs skipped because they don't exist in im_products_v2 (run INITINFC ingest first)",
      ),
    skippedWarehouseCount: z
      .number()
      .describe(
        "Number of distinct warehouse codes skipped (not found in im_warehouses)",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of im_inventory_lots rows to upsert per batch"),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
